import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { apis } from '@/config/apiConfig'
import { apiFetch } from '@/utilities/api'
import { verifyResult } from '@/utilities/utils'
import { useUser } from './use-user'
import { userKeys } from './use-user'
import { useAnalytics } from '@/contexts/AnalyticsProvider'

// Types
interface ReleaseNotesModalPreference {
  version: string
  dismissedAt: string
}

// localStorage fallback functions
const RELEASE_NOTES_MODAL_STORAGE_KEY = 'release_notes.modal_dismissed'

const getLocalStorageDismissedVersions = (): Record<string, string> => {
  try {
    return JSON.parse(
      localStorage.getItem(RELEASE_NOTES_MODAL_STORAGE_KEY) ?? '{}'
    )
  } catch (error) {
    console.error('Failed to parse release notes modal storage:', error)
    return {}
  }
}

const setLocalStorageDismissedVersion = (
  userUUID: string,
  version: string
): void => {
  try {
    const dismissedVersions = getLocalStorageDismissedVersions()
    dismissedVersions[userUUID] = version
    localStorage.setItem(
      RELEASE_NOTES_MODAL_STORAGE_KEY,
      JSON.stringify(dismissedVersions)
    )
  } catch (error) {
    console.error('Failed to store release notes modal dismissal:', error)
  }
}

// API function for saving release notes modal preference
const saveReleaseNotesModalPreference = async (
  userUUID: string,
  version: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const preferenceData: ReleaseNotesModalPreference = {
      version,
      dismissedAt: new Date().toISOString(),
    }

    void apiFetch(
      `${apis.apiDatabase.uri}user/preferences/upsert`,
      'POST',
      {
        key: 'release_notes.modal_dismissed',
        name: '',
        value: preferenceData,
      },
      result => {
        if (verifyResult(result)) {
          // Also save to localStorage as backup
          setLocalStorageDismissedVersion(userUUID, version)
          resolve()
        } else {
          // If API fails, fallback to localStorage
          console.warn(
            'Failed to save release notes modal dismissal to API, using localStorage fallback'
          )
          setLocalStorageDismissedVersion(userUUID, version)
          reject(new Error('Failed to upsert release notes preference'))
        }
      }
    )
  })
}

// Hook for tracking version notification events
export const useVersionNotificationTracking = () => {
  const { trackEvent } = useAnalytics()

  const trackModalShown = useCallback(
    (version: string) => {
      trackEvent('release_notes_modal_shown', {
        version,
      })
    },
    [trackEvent]
  )

  return {
    trackModalShown,
  }
}

// React Query mutation hook
export const useDismissVersionNotification = () => {
  const queryClient = useQueryClient()
  const { trackEvent } = useAnalytics()

  return useMutation({
    mutationFn: ({
      userUUID,
      version,
    }: {
      userUUID: string
      version: string
    }) => saveReleaseNotesModalPreference(userUUID, version),
    onSuccess: (_, { version }) => {
      // Invalidate the user query to refetch the updated preferences
      void queryClient.invalidateQueries({
        queryKey: userKeys.init(),
      })
      trackEvent('release_notes_modal_dismissed', {
        version,
      })
    },
    onError: (error, { userUUID, version }) => {
      console.error('Failed to dismiss release notes modal:', error)
      // Even if the mutation fails, we still save to localStorage
      void setLocalStorageDismissedVersion(userUUID, version)
      // Track the dismissal even if saving fails
      trackEvent('release_notes_modal_dismiss_failed', {
        version,
      })
    },
  })
}

// Utility hook for checking if a version has been dismissed
export const useIsVersionDismissed = (userUUID?: string, version?: string) => {
  const { data: user, isLoading } = useUser()

  const dismissedVersion = useMemo(() => {
    if (!user) return null
    return user.preferences?.['release_notes.modal_dismissed']?.[0]?.value
      .version
  }, [user])

  const isDismissed = useCallback((): boolean => {
    if (!userUUID || !version) return false

    if (dismissedVersion === version) {
      return true
    }

    // Fallback to localStorage if API data is not available
    const localVersions = getLocalStorageDismissedVersions()
    return localVersions[userUUID] === version
  }, [userUUID, version, dismissedVersion])

  return {
    isDismissed: isDismissed(),
    isLoading,
    dismissedVersion,
  }
}
